<script setup lang="ts">
import { Role } from '@/models/user/Role'
import { useHTTP } from '@/plugins/http'
import { onBeforeMount, onMounted, ref, Ref } from 'vue'
import { Permission, PermissionGroupEndpoint } from '@/models/user/Permission'
import { ErrorResponse } from '@/types/types'
import { FormInstance } from 'element-plus'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import InputSection from '@/components/form/InputSection.vue'
import { useI18n } from 'vue-i18n'
import permissionMessages from '@/views/hr/roles/permission_messages'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import { useSettingsStore } from '@/stores/settingsStore'
import { write } from '@rushstack/eslint-patch/lib/eslint-bulk-suppressions/bulk-suppressions-patch'

interface PermissionGroup {
  [key: string]: Permission[]
}

interface Props {
  initRole: Role
  submitAction: (role: Role) => Promise<ErrorResponse>
  successMsg: string
}

const { t } = useI18n({
  messages: {
    en: {
      ...permissionMessages.en,
      roleInfo: 'Role Info',
      placeholder: 'Example: Admin',
      permissions: 'Permissions',
      arName: "Arabic Name",
      enName: "English Name",
      arDescription: "Arabic Description",
      enDescription: "English Description",
      save: "Save",
      enNameRequired: "Please input role name in English",
      arNameRequired: "Please input role name in Arabic",
      selectAll: "Select all permissions"
    },
    ar: {
      ...permissionMessages.ar,
      roleInfo: 'بيانات المنصب',
      placeholder: 'مثلا: مدير',
      permissions: 'الصلاحيات',
      arName: "الاسم بالعربي",
      enName: "الاسم بالانجليزي",
      arDescription: "الوصف بالعربي",
      enDescription: "الوصف بالانجليزي",
      save: "حفظ",
      enNameRequired: "من فضلك ادخل اسم المنصب بالانجليزي",
      arNameRequired: "من فضلك ادخل اسم المنصب بالعربي",
      selectAll: "تحديد كل الصلاحيات"
    }
  }
})

const props = defineProps<Props>()
const http = useHTTP()
const loading = ref(false)
const error = ref({ title: '', body: '' })
const formRef = ref<FormInstance>()
const permissionGroup: Ref<PermissionGroup> = ref({})
const role = ref<Role>(new Role('', '', []))
const alertModal = useAlertModal()

const hiddenPermissionGroups: Record<string, string> = {
  '/examination/zeiss-cards': 'enable_zeiss_cards_feature'
}

async function fetchPermissionGroup() {
  const groups = await http.get<PermissionGroup>(PermissionGroupEndpoint)
  const settingsStore = useSettingsStore()
  permissionGroup.value = Object.fromEntries(
    Object.entries(groups).filter(([object]) => {
      const flag = hiddenPermissionGroups[object]
      return !flag || settingsStore.getValue(flag) === 'true'
    })
  )
}

onBeforeMount(async () => {
  try {
    loading.value = true
    await fetchPermissionGroup()
  } catch (errorResp) {
    const es = errorResp as ErrorResponse
    error.value.title = es.title
    error.value.body = es.body || ''
    loading.value = false
  } finally {
    loading.value = false
  }
})

const rules = {
  arName: [
    { required: true, message: t('arNameRequired'), trigger: 'blur' },
  ],
  enName: [
    { required: true, message: t('enNameRequired'), trigger: 'blur' },
  ],
}

const selectedPermissions = ref<Map<string, Permission>>(new Map())
onMounted(() => {
  props.initRole.permissions.forEach((p) => {
    selectedPermissions.value.set(p.id, p)
  })
  role.value = props.initRole
})

const isAllSelected = ref(false)
const selectAllPermissions = () => {
  if (isAllSelected.value) {
    isAllSelected.value = false
    selectedPermissions.value.clear()
  } else {
    isAllSelected.value = true
    for (const [pg, permissions] of Object.entries(permissionGroup.value)) {
      for (const p of permissions) {
        selectedPermissions.value.set(p.id, p)
      }
    }
  }
}

interface PermissionImplies {
  [action: string]: {
    [object: string]: { action: string, object: string }[];
  };
}

const permissionImplies: PermissionImplies = {
  // post: {
  //   "/company/register-actions": [{action: "get", object: "/reports/sales" }],
  //   "/sales/orders": [{action: "get", object: "/reports/sales" }, {action: "get", object: "/orders/sales"}, {action: "get", object: "/company/register-actions" },  {action: "post", object: "/company/registers" },  {action: "post", object: "/company/registers" }],
  //   '/customer/birthday-messages': [{action: "get", object: "/reports/customer"}]
  // },
  // get: {
  //   "/sales/orders": [{action: "get", object: "/reports/sales" }, {action: "get", object: "/company/register-actions" }, {action: "get", object: "/company/registers" }],
  //   '/customer/birthday-messages': [{action: "get", object: "/reports/customer"}]
  // }
}

const impliesPermissions = (permission: Permission) => {
  const action = permission.action.toLowerCase()
  const object = permission.object.toLowerCase()

  if (action in permissionImplies && object in permissionImplies[action]) {
    for (const implied of permissionImplies[action][object]) {
      for (const [pg, permissions] of Object.entries(permissionGroup.value)) {
        for (const p of permissions) {
          if (p.action.toLowerCase() === implied.action && p.object.toLowerCase() == implied.object) {
            selectedPermissions.value.set(p.id, p)
          }
        }
      }
    }
  }
}

const togglePermission = (permission: Permission) => {
  if (selectedPermissions.value.has(permission.id)) {
    selectedPermissions.value.delete(permission.id)
  } else {
    selectedPermissions.value.set(permission.id, permission)
    impliesPermissions(permission)
  }
}

const isSelected = (permission: Permission) => selectedPermissions.value.has(permission.id)

const beforeSubmit = (role: Role) => {
  // for all selected permissions, make sure their implied permissions are also selected
  for (const [pg, permissions] of Object.entries(permissionGroup.value)) {
    for (const p of permissions) {
      impliesPermissions(p)
    }
  }
  role.permissions = Array.from(selectedPermissions.value.values())
}

const validateSubmit = async (formRef: FormInstance | undefined) => {
  if (!formRef) {
    return
  }

    loading.value = true

    beforeSubmit(role.value)

    await formRef.validate(async (valid: boolean) => {
      if (!valid) {
        document.getElementById('main')?.scrollTo({ top: 0 })
        return false
      }

      const resp = await props.submitAction(props.initRole)
      if (resp) {
        error.value.title = resp.title
        error.value.body = resp.body || ''

        document.getElementById('main')?.scrollTo({ top: 0 })
        return false;
      }

      alertModal.showSuccess({ title: props.successMsg, body: '' })
      return true;
    })
    loading.value = false
}
</script>

<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <el-form
    v-show="!loading && !error.title"
    @submit.prevent="validateSubmit(formRef)"
    novalidate
    :model="role"
    :rules="rules"
    label-position="top"
    ref="formRef"
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
            :title="$t('actions.saveChanges')"
            isSuccess
            faIcon="save"
            @click.prevent="validateSubmit(formRef)"
          />
        </div>
      </div>
    </input-section>

    <input-section>
      <h1>{{ t('roleInfo') }}</h1>

      <el-form-item :label="t('arName')" prop="arName" class="input-label">
        <el-input
          id="name"
          v-model="role.arName"
          :placeholder="t('placeholder')"
          maxlength="127"
          show-word-limit
          clearable
          required
        >
        </el-input>
      </el-form-item>

      <el-form-item :label="t('enName')" prop="enName" class="input-label">
        <el-input
          id="name"
          v-model="role.enName"
          :placeholder="t('placeholder')"
          maxlength="127"
          show-word-limit
          clearable
          required
        >
        </el-input>
      </el-form-item>

      <el-form-item :label="t('arDescription')" prop="arDescription" class="input-label">
        <el-input
          id="name"
          v-model="role.arDescription"
          :placeholder="t('placeholder')"
          maxlength="511"
          show-word-limit
          clearable
        >
        </el-input>
      </el-form-item>

      <el-form-item :label="t('enDescription')" prop="enDescription" class="input-label">
        <el-input
          id="name"
          v-model="role.enDescription"
          :placeholder="t('placeholder')"
          maxlength="511"
          show-word-limit
          clearable
        >
        </el-input>
      </el-form-item>
    </input-section>

    <input-section>
      <h1>{{ t('permissions') }}</h1>

      <div class="bg-red-200 border-gray-400 border-2 p-4 rounded-md mb-2">
        <div
          class="-mx-4 flex cursor-pointer items-center gap-4 p-4 has-[:checked]:bg-blue-50 hover:bg-blue-50"
          @click="selectAllPermissions"
        >
          <div class="flex items-center">
            <input type="checkbox" :checked="isAllSelected" @click="selectAllPermissions" class="size-4 rounded border-gray-300" />
          </div>

          <div>
            <strong class="font-medium text-gray-900">{{ t('selectAll') }}</strong>
          </div>
        </div>
      </div>


      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div
            v-for="pg, k in permissionGroup"
            :key="pg"
            class="border-gray-400 border-2 p-4 rounded-md mb-2"
          >
            <h3 class="text-center">{{t(k)}}</h3>

            <div
              :for="p.id"
              class="-mx-4 flex cursor-pointer items-center gap-4 p-4 has-[:checked]:bg-blue-50 hover:bg-blue-50"
              v-for="p in pg"
              :key="p.id"
              @click="togglePermission(p)"
            >
              <div class="flex items-center">
                <input type="checkbox" :checked="isSelected(p)" @click="togglePermission(p)" class="size-4 rounded border-gray-300" />
              </div>

              <div>
                <strong class="font-medium text-gray-900">{{ $ctx.locale === 'ar' ? p.arName : p.enName}}</strong>

                <p class="mt-1 text-pretty text-sm text-gray-700" v-if="p.arDescription">
                  {{ $ctx.locale === 'ar' ? p.arDescription : p.enDescription }}
                </p>
              </div>
            </div>
          </div>
      </div>
    </input-section>

    <input-section>
      <div class="w-full flex justify-center mt-2 p-2">
        <a
          class="inline-block rounded border border-blue-600 px-12 py-3 text-sm font-medium text-blue-600 hover:bg-blue-600 hover:text-white focus:outline-none focus:ring active:bg-blue-500 cursor-pointer"
          @click.prevent="validateSubmit(formRef)"
        >
          {{ t('save') }}
        </a>
      </div>
    </input-section>
  </el-form>
</template>

<style scoped></style>
